/// <reference types="w3c-web-serial" />
/// <reference types="w3c-web-usb" />
import type { Board, ConnectContext, Connector, RememberedBoard } from "./board.ts";
import type { PortReturn } from "./port-return.ts";
import { awaitRelease, connectPort, type PortReturns } from "./serial-board.ts";
import { USB_SERIAL_FILTERS, UsbSerialPort } from "./usb-serial-port.ts";

/** WebUSB; available in Chromium-based browsers, Chrome for Android among them. */
const USB_API: USB | undefined = typeof navigator !== "undefined" && "usb" in navigator ? navigator.usb : undefined;

/** A board on a USB-to-UART bridge the person picks, reached with WebUSB and run as a serial board. */
export const usbConnector: Connector = {
    id: "usb",
    label: "Board (WebUSB)",
    unavailable: USB_API === undefined ? "WebUSB is only available in Chrome and Edge." : undefined,
    connect: connectUsb,
    reconnect: reconnectUsb,
};

async function connectUsb(context: ConnectContext): Promise<Board | undefined> {
    const usb = USB_API;
    if (usb === undefined) return undefined;
    let device: USBDevice;
    try {
        device = await usb.requestDevice({ filters: USB_SERIAL_FILTERS });
    } catch (e) {
        // The person closed the picker.
        if (e instanceof Error && e.name === "NotFoundError") return undefined;
        throw e;
    }
    return connectPort(portOf(usb, device), usbReturns(usb), remember(device), context);
}

/** The granted device with the remembered USB ids and serial number, when there is exactly one. */
async function reconnectUsb(remembered: RememberedBoard, context: ConnectContext): Promise<Board | undefined> {
    const usb = USB_API;
    if (usb === undefined) return undefined;
    const matching = (await usb.getDevices()).filter(
        (device) =>
            device.vendorId === remembered.vendorId &&
            device.productId === remembered.productId &&
            device.serialNumber === remembered.serialNumber,
    );
    if (matching.length !== 1) return undefined;
    const port = portOf(usb, matching[0]!);
    await awaitRelease(port, remembered);
    return connectPort(port, usbReturns(usb), remembered, context);
}

function remember(device: USBDevice): RememberedBoard {
    return {
        connector: "usb",
        vendorId: device.vendorId,
        productId: device.productId,
        serialNumber: device.serialNumber,
    };
}

/**
 * The device as a serial port that fires `disconnect` when it leaves USB.
 * The serial board and esptool-js use only the part of `SerialPort` it has.
 */
function portOf(usb: USB, device: USBDevice): SerialPort {
    const port = new UsbSerialPort(device);
    const onDisconnect = (event: USBConnectionEvent): void => {
        if (event.device !== device) return;
        usb.removeEventListener("disconnect", onDisconnect);
        port.unplugged();
    };
    usb.addEventListener("disconnect", onDisconnect);
    return port as unknown as SerialPort;
}

/** Wait for the same USB device, by its ids and serial number, to connect again. */
function usbReturns(usb: USB): PortReturns {
    return {
        await: (lost: SerialPort, found: (port: SerialPort) => void): PortReturn => {
            const gone = (lost as unknown as UsbSerialPort).device;
            const onConnect = (event: USBConnectionEvent): void => {
                if (!sameDevice(event.device, gone)) return;
                stop();
                found(portOf(usb, event.device));
            };
            const stop = (): void => usb.removeEventListener("connect", onConnect);
            usb.addEventListener("connect", onConnect);
            return { cancel: stop };
        },
    };
}

export function sameDevice(a: USBDevice, b: USBDevice): boolean {
    return a.vendorId === b.vendorId && a.productId === b.productId && a.serialNumber === b.serialNumber;
}
